import type { CanonicalLineageChangeCause } from "./canonicalLineage";
import { apiClient, getAuthToken } from "./client";
import { compareInstantsAtMicrosecondPrecision, normalizeInstantToUtcMicroseconds } from "../lib/temporalInstant";
import {
  ExportIntegrityError,
  exportBlob,
  hasExactMediaType,
  readBoundedResponseBytes,
  verifyJsonExportBytes,
  verifyOfflineHtmlBytes,
} from "./exportIntegrity";

export const IMPACT_ANALYSIS_CONTRACT_VERSION = "thread.impact.v2" as const;
export const IMPACT_ANALYSIS_PATH = "/thread/v1/provenance/impact" as const;
export const IMPACT_ANALYSIS_EXPORT_PATH = "/thread/v1/provenance/impact/export" as const;
export const IMPACT_ANALYSIS_MIN_SCHEMA_MIGRATION = "20260810210000" as const;
const IMPACT_ANALYSIS_MAX_DOCUMENT_CANONICAL_BYTES = 8_388_608;
const IMPACT_ANALYSIS_MAX_HTML_BYTES = 33_554_432;

export const IMPACT_ANALYSIS_DEFAULT_LIMITS = {
  max_depth: 32,
  max_records: 500,
  max_paths: 5000,
  max_path_steps: 5000,
} as const;

export interface ImpactAnalysisRootRef {
  entity_kind: "artifact_version";
  id: string;
}

export interface ImpactAnalysisRequest {
  project_id: string;
  root_ref: ImpactAnalysisRootRef;
  as_of?: string | null;
  recorded_as_of?: string;
  snapshot_token?: string;
  max_depth?: number;
  max_records?: number;
  max_paths?: number;
  max_path_steps?: number;
}

export interface ImpactAnalysisExportRequest extends ImpactAnalysisRequest {
  as_of: string;
  recorded_as_of: string;
  snapshot_token: string;
  expected_semantic_digest: string;
}

export type ImpactAnalysisExportFormat = "json" | "html";

export interface ImpactAnalysisExport {
  blob: Blob;
  filename: string;
  mediaType: string;
  semanticDigest: string;
}

export interface ImpactAnalysisLimits {
  max_depth: number;
  max_records: number;
  max_paths: number;
  max_path_steps: number;
}

export type ImpactLifecycleState =
  | "active"
  | "invalidated"
  | "superseded"
  | "redacted"
  | "abandoned"
  | "inapplicable";

export type ImpactRecordedState =
  | "unknown"
  | "unrecorded"
  | "fresh"
  | "stale"
  | "invalidated"
  | "superseded"
  | "pinned"
  | "deferred";

export interface ImpactStateFact {
  state: ImpactRecordedState;
  basis: "explicit_event" | "unrecorded" | "history_incomplete";
  change_cause: CanonicalLineageChangeCause | null;
  event_id: string | null;
  actor_id: string | null;
  effective_at: string | null;
  recorded_at: string | null;
  updated_at: string | null;
  reason: string | null;
}

export interface ImpactArtifactRef {
  artifact_version_id: string;
  artifact_id: string;
  artifact_kind: string;
  artifact_name: string;
  lifecycle_state: ImpactLifecycleState;
}

export interface ImpactRootLifecycle {
  state: ImpactLifecycleState;
  event_type:
    | "invalidated"
    | "superseded"
    | "redacted"
    | "abandoned"
    | "declared_inapplicable"
    | "reactivated"
    | null;
  event_id: string | null;
  effective_at: string | null;
  recorded_at: string | null;
  reason: string | null;
}

export interface ImpactPathHop {
  ordinal: number;
  input_artifact_version_id: string;
  input_binding_id: string;
  operation_id: string;
  output_binding_id: string;
  output_artifact_version_id: string;
  input_role: string;
  input_ordinal: number;
  output_role: string;
  output_ordinal: number;
  propagates_invalidity: boolean;
  binding_state: ImpactStateFact;
  operation_state: ImpactStateFact;
}

export type ImpactPathStopReason =
  | "non_propagating_binding"
  | "binding_pinned"
  | "operation_pinned";

export interface ImpactPathTerminal {
  terminal_kind: "affected" | "stopped" | "indeterminate";
  reason:
    | "propagation_enabled"
    | ImpactPathStopReason
    | "state_history_incomplete"
    | "effective_time_unavailable";
}

export interface ImpactPath {
  ordinal: number;
  path_id: string;
  subject_artifact_version_id: string;
  propagation_status: "enabled" | "stopped" | "indeterminate";
  hops: ImpactPathHop[];
  terminal: ImpactPathTerminal;
}

export interface ImpactNativeInvalidationReceipt {
  receipt_kind: "native_invalidation";
  lifecycle_event_id: string;
  idempotency_key: string;
  request_hash: string;
  audit_event_id: string;
  domain_event_id: string;
  recorded_at: string;
}

export interface ImpactImportedInvalidationAttestation {
  receipt_kind: "sealed_import_attestation";
  lifecycle_event_id: string;
  recorded_at: string;
}

export type ImpactInvalidationReceipt =
  | ImpactNativeInvalidationReceipt
  | ImpactImportedInvalidationAttestation;

export interface ImpactInvalidationEvidence {
  lifecycle_event_id: string;
  artifact_version_id: string;
  event_type: "invalidated";
  state: "invalidated";
  caused_by_artifact_version_id: string | null;
  effective_at: string;
  recorded_at: string;
  reason: string;
  cause_path_ids: string[];
  receipt: ImpactInvalidationReceipt;
}

export interface ImpactExclusion {
  exclusion_id: string;
  subject_artifact_version_id: string;
  path_id: string;
  reason: ImpactPathStopReason;
  step_ordinal: number;
}

export interface ImpactSubject {
  ordinal: number;
  subject_ref: ImpactArtifactRef;
  minimum_depth: number;
  reachability_status: "reachable";
  propagation_status: "enabled" | "stopped" | "indeterminate";
  explicit_invalidation_status: "recorded" | "not_recorded" | "indeterminate";
  path_ids: string[];
  exclusion_ids: string[];
  invalidation_evidence: ImpactInvalidationEvidence | null;
}

export type ImpactGapCode =
  | "max_depth_reached"
  | "max_records_reached"
  | "max_paths_reached"
  | "max_path_steps_reached"
  | "cycle_detected"
  | "state_history_incomplete"
  | "effective_time_unavailable"
  | "invalidation_receipt_unavailable"
  | "unknown_record_type"
  | "unknown_relationship_type"
  | "unsupported_semantics";

export interface ImpactGap {
  code: ImpactGapCode;
  subject_artifact_version_id: string | null;
}

export interface ImpactCompleteness {
  traversal: "complete" | "incomplete";
  propagation: "complete" | "incomplete";
  lifecycle_evidence: "complete" | "incomplete";
  temporal: "effective_and_recorded_time";
  gaps: ImpactGap[];
  redaction_terminal: {
    terminal_kind: "redacted";
    reason: "restricted_evidence_omitted";
  } | null;
}

export interface ImpactSummary {
  root_excluded: true;
  reachable_count: number;
  propagation_closure_count: number;
  propagation_enabled_count: number;
  explicitly_invalidated_count: number;
  potential_only_count: number;
  stopped_count: number;
  indeterminate_count: number;
  exclusion_count: number;
}

export interface ImpactAnalysisDocument {
  contract_version: typeof IMPACT_ANALYSIS_CONTRACT_VERSION;
  snapshot: {
    project_id: string;
    root_ref: ImpactAnalysisRootRef;
    effective_as_of: string;
    recorded_as_of: string;
    snapshot_token: string;
    temporal_basis: "effective_and_recorded_time";
    source_contract_version: "thread.lineage.v2";
    limits: ImpactAnalysisLimits;
  };
  root_cause: ImpactArtifactRef;
  root_lifecycle: ImpactRootLifecycle;
  summary: ImpactSummary;
  subjects: ImpactSubject[];
  paths: ImpactPath[];
  exclusions: ImpactExclusion[];
  completeness: ImpactCompleteness;
}

export interface ImpactAnalysisResult {
  document: ImpactAnalysisDocument;
  semantic_identity: {
    digest: string;
    canonicalizer: "rfc8785";
    canonicalizer_version: "1";
    byte_length: number;
    media_type: "application/vnd.thread.impact.v2+json";
  };
}

export interface ImpactAnalysisEnvelope {
  schema_version: "thread.api.v1";
  request_id: string;
  data: ImpactAnalysisResult;
  page: null;
  provenance: {
    source: "thread_core";
    projection_version: typeof IMPACT_ANALYSIS_CONTRACT_VERSION;
    schema_migration: string;
  };
  warnings: string[];
}

type UnknownRecord = Record<string, unknown>;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const CANONICAL_UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const FIXED_UTC_TIMESTAMP_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{6}Z$/;
const AWARE_TIMESTAMP_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;
const AWARE_TIMESTAMP_PARTS = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?(Z|([+-])(\d{2}):(\d{2}))$/;
const SHA256_PATTERN = /^sha256:[0-9a-f]{64}$/;
const SNAPSHOT_TOKEN_PATTERN = /^ts1\.[0-9a-f]{16}\.[A-Za-z0-9_-]{39,4060}$/;
const IDEMPOTENCY_BOUNDARY_WHITESPACE_PATTERN = /^[\u0009-\u000d\u001c-\u0020\u0085\u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000]|[\u0009-\u000d\u001c-\u0020\u0085\u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000]$/u;

const LIFECYCLE_STATES: readonly ImpactLifecycleState[] = [
  "active", "invalidated", "superseded", "redacted", "abandoned", "inapplicable",
];
const RECORDED_STATES: readonly ImpactRecordedState[] = [
  "unknown", "unrecorded", "fresh", "stale", "invalidated", "superseded", "pinned", "deferred",
];
const CHANGE_CAUSES: readonly CanonicalLineageChangeCause[] = [
  "geometry_changed", "mesh_changed", "solver_version_changed", "coordinate_frame_changed",
  "unit_system_changed", "schema_changed", "reference_area_changed", "parameter_changed",
  "metadata_only_changed", "comment_only_changed", "unknown_impact", "manual_override",
];
const GAP_CODES: readonly ImpactGapCode[] = [
  "max_depth_reached", "max_records_reached", "max_paths_reached", "max_path_steps_reached",
  "cycle_detected", "state_history_incomplete", "effective_time_unavailable",
  "invalidation_receipt_unavailable", "unknown_record_type",
  "unknown_relationship_type", "unsupported_semantics",
];

export class ImpactAnalysisValidationError extends Error {
  readonly path: string;

  constructor(path: string, expectation: string) {
    super(`${path}: ${expectation}`);
    this.name = "ImpactAnalysisValidationError";
    this.path = path;
  }
}

export class ImpactAnalysisChangedError extends Error {
  constructor() {
    super("Impact analysis changed while the export was assembled.");
    this.name = "ImpactAnalysisChangedError";
  }
}

function fail(path: string, expectation: string): never {
  throw new ImpactAnalysisValidationError(path, expectation);
}

function exactRecord(value: unknown, path: string, keys: readonly string[], optional: readonly string[] = []): UnknownRecord {
  if (value === null || typeof value !== "object" || Array.isArray(value)) fail(path, "expected an object");
  const item = value as UnknownRecord;
  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(item, key)) fail(`${path}.${key}`, "missing required field");
  }
  const allowed = new Set([...keys, ...optional]);
  for (const key of Object.keys(item)) {
    if (!allowed.has(key)) fail(`${path}.${key}`, "unknown field on a known contract object");
  }
  return item;
}

function array(value: unknown, path: string, max: number, min = 0): unknown[] {
  if (!Array.isArray(value) || value.length < min || value.length > max) {
    fail(path, `expected between ${min} and ${max} items`);
  }
  return value;
}

function text(value: unknown, path: string, max = Number.MAX_SAFE_INTEGER, allowEmpty = false): string {
  if (typeof value !== "string" || (!allowEmpty && value.length === 0) || [...value].length > max) {
    fail(path, `expected ${allowEmpty ? "a" : "a non-empty"} string of at most ${max} characters`);
  }
  return value;
}

function nullableNonEmptyText(value: unknown, path: string, max: number): string | null {
  return value === null ? null : text(value, path, max);
}

function literal<T extends string>(value: unknown, path: string, choices: readonly T[]): T {
  if (typeof value !== "string" || !choices.includes(value as T)) fail(path, `expected one of: ${choices.join(", ")}`);
  return value as T;
}

function integer(value: unknown, path: string, min: number, max: number): number {
  if (typeof value !== "number" || !Number.isInteger(value) || value < min || value > max) {
    fail(path, `expected an integer between ${min} and ${max}`);
  }
  return value;
}

function boolean(value: unknown, path: string): boolean {
  if (typeof value !== "boolean") fail(path, "expected a boolean");
  return value;
}

function requestUuid(value: unknown, path: string): string {
  const result = text(value, path, 36);
  if (!UUID_PATTERN.test(result)) fail(path, "expected a UUID");
  return result;
}

function uuid(value: unknown, path: string): string {
  const result = text(value, path, 36);
  if (!CANONICAL_UUID_PATTERN.test(result)) fail(path, "expected a canonical lowercase UUID");
  return result;
}

function nullableUuid(value: unknown, path: string): string | null {
  return value === null ? null : uuid(value, path);
}

function digest(value: unknown, path: string): string {
  const result = text(value, path, 71);
  if (!SHA256_PATTERN.test(result)) fail(path, "expected a lowercase SHA-256 digest");
  return result;
}

function idempotencyKey(value: unknown, path: string): string {
  const result = text(value, path, 200);
  if (IDEMPOTENCY_BOUNDARY_WHITESPACE_PATTERN.test(result)) fail(path, "must not contain boundary whitespace");
  return result;
}

function validAwareTimestampComponents(value: string): boolean {
  const match = AWARE_TIMESTAMP_PARTS.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6]);
  const offsetHour = match[8] === "Z" ? 0 : Number(match[10]);
  const offsetMinute = match[8] === "Z" ? 0 : Number(match[11]);
  const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const monthLengths = [31, leapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const normalizedMillis = Date.parse(value);
  const normalizedUtcYear = Number.isFinite(normalizedMillis) ? new Date(normalizedMillis).getUTCFullYear() : 0;
  return year >= 1
    && month >= 1 && month <= 12
    && day >= 1 && day <= monthLengths[month - 1]!
    && hour <= 23 && minute <= 59 && second <= 59
    && offsetHour <= 23 && offsetMinute <= 59
    && normalizedUtcYear >= 1 && normalizedUtcYear <= 9999;
}

function timestamp(value: unknown, path: string): string {
  const result = text(value, path, 32);
  if (!FIXED_UTC_TIMESTAMP_PATTERN.test(result) || !validAwareTimestampComponents(result)) {
    fail(path, "expected a UTC timestamp with exactly six fractional digits");
  }
  return result;
}

function nullableTimestamp(value: unknown, path: string): string | null {
  return value === null ? null : timestamp(value, path);
}

function awareTimestamp(value: unknown, path: string): string {
  const result = text(value, path);
  const requestYear = Number(result.slice(0, 4));
  const yearAllowed = (requestYear >= 2 && requestYear <= 9998)
    || ((requestYear === 1 || requestYear === 9999) && result.endsWith("Z"));
  if (!AWARE_TIMESTAMP_PATTERN.test(result) || !validAwareTimestampComponents(result) || !yearAllowed) {
    fail(path, "expected an ISO-8601 timestamp with a timezone offset");
  }
  return result;
}

function snapshotToken(value: unknown, path: string): string {
  const result = text(value, path, 4096);
  if (!SNAPSHOT_TOKEN_PATTERN.test(result)) fail(path, "expected a canonical keyring-bound ts1 snapshot token");
  return result;
}

function limits(value: unknown, path: string): ImpactAnalysisLimits {
  const item = exactRecord(value, path, ["max_depth", "max_records", "max_paths", "max_path_steps"]);
  integer(item.max_depth, `${path}.max_depth`, 1, 32);
  integer(item.max_records, `${path}.max_records`, 1, 500);
  integer(item.max_paths, `${path}.max_paths`, 1, 5000);
  integer(item.max_path_steps, `${path}.max_path_steps`, 1, 10000);
  return value as ImpactAnalysisLimits;
}

function rootRef(value: unknown, path: string, request = false): ImpactAnalysisRootRef {
  const item = exactRecord(value, path, ["entity_kind", "id"]);
  literal(item.entity_kind, `${path}.entity_kind`, ["artifact_version"]);
  (request ? requestUuid : uuid)(item.id, `${path}.id`);
  return value as ImpactAnalysisRootRef;
}

export function validateImpactAnalysisRequest(value: unknown, path = "request"): asserts value is ImpactAnalysisRequest {
  const item = exactRecord(
    value,
    path,
    ["project_id", "root_ref"],
    ["as_of", "recorded_as_of", "snapshot_token", "max_depth", "max_records", "max_paths", "max_path_steps"],
  );
  requestUuid(item.project_id, `${path}.project_id`);
  rootRef(item.root_ref, `${path}.root_ref`, true);
  const effectiveAsOf = item.as_of !== undefined && item.as_of !== null
    ? awareTimestamp(item.as_of, `${path}.as_of`)
    : null;
  const hasRecordedAsOf = item.recorded_as_of !== undefined;
  const hasSnapshotToken = item.snapshot_token !== undefined;
  if (hasRecordedAsOf !== hasSnapshotToken) fail(path, "recorded_as_of and snapshot_token must be supplied together");
  if (hasRecordedAsOf) {
    const recordedAsOf = timestamp(item.recorded_as_of, `${path}.recorded_as_of`);
    snapshotToken(item.snapshot_token, `${path}.snapshot_token`);
    if (effectiveAsOf === null) fail(`${path}.as_of`, "is required for frozen replay");
    if (compareInstantsAtMicrosecondPrecision(effectiveAsOf, recordedAsOf) > 0) {
      fail(`${path}.as_of`, "must not be later than recorded_as_of");
    }
  }
  if (item.max_depth !== undefined) integer(item.max_depth, `${path}.max_depth`, 1, 32);
  if (item.max_records !== undefined) integer(item.max_records, `${path}.max_records`, 1, 500);
  if (item.max_paths !== undefined) integer(item.max_paths, `${path}.max_paths`, 1, 5000);
  if (item.max_path_steps !== undefined) integer(item.max_path_steps, `${path}.max_path_steps`, 1, 10000);
}

export function normalizeImpactAnalysisRequest(request: ImpactAnalysisRequest): ImpactAnalysisRequest {
  validateImpactAnalysisRequest(request);
  return {
    ...request,
    ...(typeof request.as_of === "string" ? { as_of: normalizeInstantToUtcMicroseconds(request.as_of) } : {}),
    project_id: request.project_id.toLowerCase(),
    root_ref: { entity_kind: "artifact_version", id: request.root_ref.id.toLowerCase() },
  };
}

function validateImpactAnalysisExportRequest(value: unknown): asserts value is ImpactAnalysisExportRequest {
  const item = exactRecord(
    value,
    "request",
    ["project_id", "root_ref", "as_of", "recorded_as_of", "snapshot_token", "expected_semantic_digest"],
    ["max_depth", "max_records", "max_paths", "max_path_steps"],
  );
  const analysisRequest = { ...item };
  delete analysisRequest.expected_semantic_digest;
  validateImpactAnalysisRequest(analysisRequest);
  awareTimestamp(item.as_of, "request.as_of");
  digest(item.expected_semantic_digest, "request.expected_semantic_digest");
}

function stateFact(value: unknown, path: string): ImpactStateFact {
  const item = exactRecord(value, path, [
    "state", "basis", "change_cause", "event_id", "actor_id", "effective_at", "recorded_at", "updated_at", "reason",
  ]);
  const state = literal(item.state, `${path}.state`, RECORDED_STATES);
  const basis = literal(item.basis, `${path}.basis`, ["explicit_event", "unrecorded", "history_incomplete"]);
  const cause = item.change_cause === null
    ? null
    : literal(item.change_cause, `${path}.change_cause`, CHANGE_CAUSES);
  const eventId = nullableUuid(item.event_id, `${path}.event_id`);
  const actorId = nullableUuid(item.actor_id, `${path}.actor_id`);
  const effectiveAt = nullableTimestamp(item.effective_at, `${path}.effective_at`);
  const recordedAt = nullableTimestamp(item.recorded_at, `${path}.recorded_at`);
  const updatedAt = nullableTimestamp(item.updated_at, `${path}.updated_at`);
  const reason = nullableNonEmptyText(item.reason, `${path}.reason`, 2000);
  if (basis === "unrecorded") {
    if (state !== "unrecorded" || [cause, eventId, actorId, effectiveAt, recordedAt, updatedAt, reason].some((entry) => entry !== null)) {
      fail(path, "unrecorded state cannot carry event fields");
    }
  } else if (basis === "history_incomplete") {
    if (state !== "unknown" || [cause, eventId, actorId, effectiveAt, recordedAt, updatedAt, reason].some((entry) => entry !== null)) {
      fail(path, "incomplete state history cannot claim state or event fields");
    }
  } else if (state === "unrecorded" || state === "unknown" || cause === null || eventId === null || actorId === null || recordedAt === null) {
    fail(path, "explicit state requires a recorded state, cause, event, actor, and recorded_at");
  }
  // updated_at and ledger recorded_at are independent system clocks. A row
  // transaction timestamp can legitimately precede the later ledger batch.
  return value as ImpactStateFact;
}

function pathHop(value: unknown, path: string): ImpactPathHop {
  const item = exactRecord(value, path, [
    "ordinal", "input_artifact_version_id", "input_binding_id", "operation_id", "output_binding_id",
    "output_artifact_version_id", "input_role", "input_ordinal", "output_role", "output_ordinal",
    "propagates_invalidity", "binding_state", "operation_state",
  ]);
  integer(item.ordinal, `${path}.ordinal`, 0, 31);
  uuid(item.input_artifact_version_id, `${path}.input_artifact_version_id`);
  uuid(item.input_binding_id, `${path}.input_binding_id`);
  uuid(item.operation_id, `${path}.operation_id`);
  uuid(item.output_binding_id, `${path}.output_binding_id`);
  uuid(item.output_artifact_version_id, `${path}.output_artifact_version_id`);
  text(item.input_role, `${path}.input_role`, 100);
  integer(item.input_ordinal, `${path}.input_ordinal`, 0, Number.MAX_SAFE_INTEGER);
  text(item.output_role, `${path}.output_role`, 100);
  integer(item.output_ordinal, `${path}.output_ordinal`, 0, Number.MAX_SAFE_INTEGER);
  boolean(item.propagates_invalidity, `${path}.propagates_invalidity`);
  stateFact(item.binding_state, `${path}.binding_state`);
  stateFact(item.operation_state, `${path}.operation_state`);
  return value as ImpactPathHop;
}

function impactPath(value: unknown, path: string): ImpactPath {
  const item = exactRecord(value, path, ["ordinal", "path_id", "subject_artifact_version_id", "propagation_status", "hops", "terminal"]);
  integer(item.ordinal, `${path}.ordinal`, 0, 4999);
  digest(item.path_id, `${path}.path_id`);
  const subjectId = uuid(item.subject_artifact_version_id, `${path}.subject_artifact_version_id`);
  const status = literal(item.propagation_status, `${path}.propagation_status`, ["enabled", "stopped", "indeterminate"]);
  const hops = array(item.hops, `${path}.hops`, 32, 1).map((hop, index) => pathHop(hop, `${path}.hops[${index}]`));
  if (hops.some((hop, index) => hop.ordinal !== index)) fail(`${path}.hops`, "hop ordinals must be contiguous");
  if (hops.some((hop, index) => index > 0 && hops[index - 1]!.output_artifact_version_id !== hop.input_artifact_version_id)) {
    fail(`${path}.hops`, "hops must form one causal chain");
  }
  const versionChain = [hops[0]!.input_artifact_version_id, ...hops.map((hop) => hop.output_artifact_version_id)];
  if (new Set(versionChain).size !== versionChain.length) fail(`${path}.hops`, "causal paths cannot repeat an ArtifactVersion");
  if (hops[hops.length - 1]!.output_artifact_version_id !== subjectId) fail(path, "path must terminate at its subject");
  const terminal = exactRecord(item.terminal, `${path}.terminal`, ["terminal_kind", "reason"]);
  const terminalKind = literal(terminal.terminal_kind, `${path}.terminal.terminal_kind`, ["affected", "stopped", "indeterminate"]);
  const reason = literal(terminal.reason, `${path}.terminal.reason`, [
    "propagation_enabled", "non_propagating_binding", "binding_pinned", "operation_pinned",
    "state_history_incomplete", "effective_time_unavailable",
  ]);
  const enabledReason = reason === "propagation_enabled";
  const stoppedReason = reason === "non_propagating_binding" || reason === "binding_pinned" || reason === "operation_pinned";
  const expectedStatus = terminalKind === "affected" ? "enabled" : terminalKind === "stopped" ? "stopped" : "indeterminate";
  if ((terminalKind === "affected") !== enabledReason || (terminalKind === "stopped") !== stoppedReason || status !== expectedStatus) {
    fail(`${path}.terminal`, "terminal kind, reason, and propagation status must agree");
  }
  let derivedStop: ImpactPathTerminal["reason"] | null = null;
  let derivedStopOrdinal: number | null = null;
  let derivedIndeterminate: ImpactPathTerminal["reason"] | null = null;
  for (const [hopIndex, hop] of hops.entries()) {
    if (derivedStop === null) {
      if (!hop.propagates_invalidity) derivedStop = "non_propagating_binding";
      else if (hop.binding_state.state === "pinned" && hop.binding_state.effective_at !== null && hop.binding_state.updated_at !== null) derivedStop = "binding_pinned";
      else if (hop.operation_state.state === "pinned" && hop.operation_state.effective_at !== null && hop.operation_state.updated_at !== null) derivedStop = "operation_pinned";
      if (derivedStop !== null) derivedStopOrdinal = hopIndex;
    }
    if (derivedIndeterminate === null) {
      const states = [hop.binding_state, hop.operation_state];
      if (states.some((state) => state.basis === "history_incomplete")) derivedIndeterminate = "state_history_incomplete";
      else if (states.some((state) => state.basis === "explicit_event" && state.effective_at === null)) derivedIndeterminate = "effective_time_unavailable";
      else if (states.some((state) => state.basis === "explicit_event" && state.updated_at === null)) derivedIndeterminate = "state_history_incomplete";
    }
  }
  if (derivedStopOrdinal !== null && derivedStopOrdinal !== hops.length - 1) {
    fail(`${path}.hops`, "a path must end at its first decisive propagation stop");
  }
  const derived = derivedStop !== null
    ? { status: "stopped", kind: "stopped", reason: derivedStop }
    : derivedIndeterminate !== null
      ? { status: "indeterminate", kind: "indeterminate", reason: derivedIndeterminate }
      : { status: "enabled", kind: "affected", reason: "propagation_enabled" };
  if (status !== derived.status || terminalKind !== derived.kind || reason !== derived.reason) {
    fail(path, "path status must be derived from its exact hop facts");
  }
  return value as ImpactPath;
}

function receipt(value: unknown, path: string): ImpactInvalidationReceipt {
  const base = exactRecord(value, path, ["receipt_kind", "lifecycle_event_id", "recorded_at"], [
    "idempotency_key", "request_hash", "audit_event_id", "domain_event_id",
  ]);
  const kind = literal(base.receipt_kind, `${path}.receipt_kind`, ["native_invalidation", "sealed_import_attestation"]);
  uuid(base.lifecycle_event_id, `${path}.lifecycle_event_id`);
  timestamp(base.recorded_at, `${path}.recorded_at`);
  if (kind === "native_invalidation") {
    const item = exactRecord(value, path, [
      "receipt_kind", "lifecycle_event_id", "idempotency_key", "request_hash", "audit_event_id", "domain_event_id", "recorded_at",
    ]);
    idempotencyKey(item.idempotency_key, `${path}.idempotency_key`);
    digest(item.request_hash, `${path}.request_hash`);
    uuid(item.audit_event_id, `${path}.audit_event_id`);
    uuid(item.domain_event_id, `${path}.domain_event_id`);
  } else {
    exactRecord(value, path, ["receipt_kind", "lifecycle_event_id", "recorded_at"]);
  }
  return value as ImpactInvalidationReceipt;
}

function invalidationEvidence(value: unknown, path: string): ImpactInvalidationEvidence {
  const item = exactRecord(value, path, [
    "lifecycle_event_id", "artifact_version_id", "event_type", "state", "caused_by_artifact_version_id",
    "effective_at", "recorded_at", "reason", "cause_path_ids", "receipt",
  ]);
  const lifecycleEventId = uuid(item.lifecycle_event_id, `${path}.lifecycle_event_id`);
  uuid(item.artifact_version_id, `${path}.artifact_version_id`);
  literal(item.event_type, `${path}.event_type`, ["invalidated"]);
  literal(item.state, `${path}.state`, ["invalidated"]);
  nullableUuid(item.caused_by_artifact_version_id, `${path}.caused_by_artifact_version_id`);
  timestamp(item.effective_at, `${path}.effective_at`);
  const recordedAt = timestamp(item.recorded_at, `${path}.recorded_at`);
  text(item.reason, `${path}.reason`, 2000);
  const pathIds = array(item.cause_path_ids, `${path}.cause_path_ids`, 5000, 1).map((entry, index) => digest(entry, `${path}.cause_path_ids[${index}]`));
  if (pathIds.some((entry, index) => index > 0 && pathIds[index - 1]! >= entry)) fail(`${path}.cause_path_ids`, "expected sorted unique path IDs");
  const evidenceReceipt = receipt(item.receipt, `${path}.receipt`);
  if (evidenceReceipt.lifecycle_event_id !== lifecycleEventId) fail(`${path}.receipt.lifecycle_event_id`, "must identify the evidence lifecycle event");
  if (evidenceReceipt.recorded_at < recordedAt) fail(`${path}.receipt.recorded_at`, "must not precede the lifecycle event");
  return value as ImpactInvalidationEvidence;
}

function subject(value: unknown, path: string): ImpactSubject {
  const item = exactRecord(value, path, [
    "ordinal", "subject_ref", "minimum_depth", "reachability_status", "propagation_status",
    "explicit_invalidation_status", "path_ids", "exclusion_ids", "invalidation_evidence",
  ]);
  integer(item.ordinal, `${path}.ordinal`, 0, 499);
  const ref = exactRecord(item.subject_ref, `${path}.subject_ref`, [
    "artifact_version_id", "artifact_id", "artifact_kind", "artifact_name", "lifecycle_state",
  ]);
  const subjectId = uuid(ref.artifact_version_id, `${path}.subject_ref.artifact_version_id`);
  uuid(ref.artifact_id, `${path}.subject_ref.artifact_id`);
  text(ref.artifact_kind, `${path}.subject_ref.artifact_kind`, 100);
  text(ref.artifact_name, `${path}.subject_ref.artifact_name`, 500);
  literal(ref.lifecycle_state, `${path}.subject_ref.lifecycle_state`, LIFECYCLE_STATES);
  integer(item.minimum_depth, `${path}.minimum_depth`, 1, 32);
  literal(item.reachability_status, `${path}.reachability_status`, ["reachable"]);
  literal(item.propagation_status, `${path}.propagation_status`, ["enabled", "stopped", "indeterminate"]);
  const explicitStatus = literal(item.explicit_invalidation_status, `${path}.explicit_invalidation_status`, ["recorded", "not_recorded", "indeterminate"]);
  const pathIds = array(item.path_ids, `${path}.path_ids`, 5000, 1).map((entry, index) => digest(entry, `${path}.path_ids[${index}]`));
  const exclusionIds = array(item.exclusion_ids, `${path}.exclusion_ids`, 5000).map((entry, index) => digest(entry, `${path}.exclusion_ids[${index}]`));
  for (const [values, field] of [[pathIds, "path_ids"], [exclusionIds, "exclusion_ids"]] as const) {
    if (values.some((entry, index) => index > 0 && values[index - 1]! >= entry)) fail(`${path}.${field}`, "expected sorted unique IDs");
  }
  const evidence = item.invalidation_evidence === null ? null : invalidationEvidence(item.invalidation_evidence, `${path}.invalidation_evidence`);
  if ((explicitStatus === "recorded") !== (evidence !== null)) fail(`${path}.invalidation_evidence`, "must exist exactly when explicit invalidation is recorded");
  if (evidence && evidence.artifact_version_id !== subjectId) fail(`${path}.invalidation_evidence.artifact_version_id`, "must identify the subject");
  return value as ImpactSubject;
}

function exclusion(value: unknown, path: string): ImpactExclusion {
  const item = exactRecord(value, path, ["exclusion_id", "subject_artifact_version_id", "path_id", "reason", "step_ordinal"]);
  digest(item.exclusion_id, `${path}.exclusion_id`);
  uuid(item.subject_artifact_version_id, `${path}.subject_artifact_version_id`);
  digest(item.path_id, `${path}.path_id`);
  literal(item.reason, `${path}.reason`, ["non_propagating_binding", "binding_pinned", "operation_pinned"]);
  integer(item.step_ordinal, `${path}.step_ordinal`, 0, 31);
  return value as ImpactExclusion;
}

function artifactRef(value: unknown, path: string): ImpactArtifactRef {
  const item = exactRecord(value, path, [
    "artifact_version_id", "artifact_id", "artifact_kind", "artifact_name", "lifecycle_state",
  ]);
  uuid(item.artifact_version_id, `${path}.artifact_version_id`);
  uuid(item.artifact_id, `${path}.artifact_id`);
  text(item.artifact_kind, `${path}.artifact_kind`, 100);
  text(item.artifact_name, `${path}.artifact_name`, 500);
  literal(item.lifecycle_state, `${path}.lifecycle_state`, LIFECYCLE_STATES);
  return value as ImpactArtifactRef;
}

function rootLifecycle(value: unknown, path: string): ImpactRootLifecycle {
  const item = exactRecord(value, path, ["state", "event_type", "event_id", "effective_at", "recorded_at", "reason"]);
  const state = literal(item.state, `${path}.state`, LIFECYCLE_STATES);
  const eventType = item.event_type === null ? null : literal(item.event_type, `${path}.event_type`, [
    "invalidated", "superseded", "redacted", "abandoned", "declared_inapplicable", "reactivated",
  ]);
  const eventId = nullableUuid(item.event_id, `${path}.event_id`);
  const effectiveAt = nullableTimestamp(item.effective_at, `${path}.effective_at`);
  const recordedAt = nullableTimestamp(item.recorded_at, `${path}.recorded_at`);
  const reason = nullableNonEmptyText(item.reason, `${path}.reason`, 2000);
  if (eventType === null) {
    if (state !== "active" || [eventId, effectiveAt, recordedAt, reason].some((entry) => entry !== null)) {
      fail(path, "a null root lifecycle event must be the closed active/no-event shape");
    }
  } else {
    if ([eventId, effectiveAt, recordedAt, reason].some((entry) => entry === null)) fail(path, "root lifecycle event fields must be complete");
    const expectedState = eventType === "declared_inapplicable" ? "inapplicable" : eventType === "reactivated" ? "active" : eventType;
    if (state !== expectedState) fail(path, "root lifecycle event_type and state are inconsistent");
  }
  return value as ImpactRootLifecycle;
}

function parseDocument(value: unknown, path: string): ImpactAnalysisDocument {
  const item = exactRecord(value, path, [
    "contract_version", "snapshot", "root_cause", "root_lifecycle", "summary", "subjects", "paths",
    "exclusions", "completeness",
  ]);
  literal(item.contract_version, `${path}.contract_version`, [IMPACT_ANALYSIS_CONTRACT_VERSION]);
  const snapshot = exactRecord(item.snapshot, `${path}.snapshot`, [
    "project_id", "root_ref", "effective_as_of", "recorded_as_of", "snapshot_token",
    "temporal_basis", "source_contract_version", "limits",
  ]);
  uuid(snapshot.project_id, `${path}.snapshot.project_id`);
  const root = rootRef(snapshot.root_ref, `${path}.snapshot.root_ref`);
  const effectiveAsOf = timestamp(snapshot.effective_as_of, `${path}.snapshot.effective_as_of`);
  const recordedAsOf = timestamp(snapshot.recorded_as_of, `${path}.snapshot.recorded_as_of`);
  if (compareInstantsAtMicrosecondPrecision(effectiveAsOf, recordedAsOf) > 0) {
    fail(`${path}.snapshot.effective_as_of`, "must not be later than recorded_as_of");
  }
  snapshotToken(snapshot.snapshot_token, `${path}.snapshot.snapshot_token`);
  literal(snapshot.temporal_basis, `${path}.snapshot.temporal_basis`, ["effective_and_recorded_time"]);
  literal(snapshot.source_contract_version, `${path}.snapshot.source_contract_version`, ["thread.lineage.v2"]);
  const resultLimits = limits(snapshot.limits, `${path}.snapshot.limits`);
  const rootCause = artifactRef(item.root_cause, `${path}.root_cause`);
  if (rootCause.artifact_version_id !== root.id) fail(`${path}.root_cause.artifact_version_id`, "must identify snapshot.root_ref");
  const rootLifecycleFact = rootLifecycle(item.root_lifecycle, `${path}.root_lifecycle`);
  if (rootLifecycleFact.state !== rootCause.lifecycle_state) fail(`${path}.root_lifecycle.state`, "must match root_cause.lifecycle_state");

  const subjects = array(item.subjects, `${path}.subjects`, resultLimits.max_records).map((entry, index) => subject(entry, `${path}.subjects[${index}]`));
  const paths = array(item.paths, `${path}.paths`, resultLimits.max_paths).map((entry, index) => impactPath(entry, `${path}.paths[${index}]`));
  const exclusions = array(item.exclusions, `${path}.exclusions`, resultLimits.max_paths).map((entry, index) => exclusion(entry, `${path}.exclusions[${index}]`));
  if (subjects.some((entry, index) => entry.ordinal !== index)) fail(`${path}.subjects`, "subject ordinals must be contiguous");
  const subjectIds = new Set(subjects.map((entry) => entry.subject_ref.artifact_version_id));
  if (subjectIds.size !== subjects.length) fail(`${path}.subjects`, "subject ArtifactVersion IDs must be unique");
  if (paths.some((entry, index) => entry.ordinal !== index)) fail(`${path}.paths`, "path ordinals must be contiguous");
  if (subjects.some((entry) => entry.subject_ref.artifact_version_id === root.id)) fail(`${path}.subjects`, "the root must be excluded");
  if (paths.some((entry) => entry.hops[0]!.input_artifact_version_id !== root.id)) fail(`${path}.paths`, "every path must start at snapshot.root_ref");
  if (paths.some((entry) => entry.hops.length > resultLimits.max_depth)) fail(`${path}.paths`, "path depth exceeds snapshot.limits.max_depth");
  if (paths.some((entry) => !subjectIds.has(entry.subject_artifact_version_id))) fail(`${path}.paths`, "every path subject must be emitted in subjects");
  if (exclusions.some((entry) => !subjectIds.has(entry.subject_artifact_version_id))) fail(`${path}.exclusions`, "every exclusion subject must be emitted in subjects");
  if (paths.reduce((count, entry) => count + entry.hops.length, 0) > resultLimits.max_path_steps) fail(`${path}.paths`, "path hops exceed max_path_steps");
  if (rootLifecycleFact.effective_at !== null && rootLifecycleFact.effective_at > effectiveAsOf) {
    fail(`${path}.root_lifecycle.effective_at`, "must not exceed snapshot.effective_as_of");
  }
  if (rootLifecycleFact.recorded_at !== null && rootLifecycleFact.recorded_at > recordedAsOf) {
    fail(`${path}.root_lifecycle.recorded_at`, "must not exceed snapshot.recorded_as_of");
  }
  for (const [pathIndex, entry] of paths.entries()) {
    for (const [hopIndex, hop] of entry.hops.entries()) {
      for (const [stateName, state] of [["binding_state", hop.binding_state], ["operation_state", hop.operation_state]] as const) {
        if (state.effective_at !== null && state.effective_at > effectiveAsOf) {
          fail(`${path}.paths[${pathIndex}].hops[${hopIndex}].${stateName}.effective_at`, "must not exceed snapshot.effective_as_of");
        }
        for (const field of ["recorded_at", "updated_at"] as const) {
          if (state[field] !== null && state[field]! > recordedAsOf) fail(`${path}.paths[${pathIndex}].hops[${hopIndex}].${stateName}.${field}`, "must not exceed snapshot.recorded_as_of");
        }
      }
    }
  }

  const pathsById = new Map(paths.map((entry) => [entry.path_id, entry]));
  const exclusionsById = new Map(exclusions.map((entry) => [entry.exclusion_id, entry]));
  const exclusionsByPathId = new Map<string, ImpactExclusion[]>();
  for (const entry of exclusions) {
    const existing = exclusionsByPathId.get(entry.path_id);
    if (existing) existing.push(entry);
    else exclusionsByPathId.set(entry.path_id, [entry]);
  }
  if (pathsById.size !== paths.length) fail(`${path}.paths`, "path IDs must be unique");
  if (exclusionsById.size !== exclusions.length) fail(`${path}.exclusions`, "exclusion IDs must be unique");
  const completenessHeader = exactRecord(item.completeness, `${path}.completeness`, [
    "traversal", "propagation", "lifecycle_evidence", "temporal", "gaps", "redaction_terminal",
  ]);
  const traversal = literal(completenessHeader.traversal, `${path}.completeness.traversal`, ["complete", "incomplete"]);
  const propagationCompleteness = literal(completenessHeader.propagation, `${path}.completeness.propagation`, ["complete", "incomplete"]);
  const lifecycleCompleteness = literal(completenessHeader.lifecycle_evidence, `${path}.completeness.lifecycle_evidence`, ["complete", "incomplete"]);
  literal(completenessHeader.temporal, `${path}.completeness.temporal`, ["effective_and_recorded_time"]);
  const gaps = array(completenessHeader.gaps, `${path}.completeness.gaps`, 5000).map((entry, index) => {
    const gap = exactRecord(entry, `${path}.completeness.gaps[${index}]`, ["code", "subject_artifact_version_id"]);
    literal(gap.code, `${path}.completeness.gaps[${index}].code`, GAP_CODES);
    nullableUuid(gap.subject_artifact_version_id, `${path}.completeness.gaps[${index}].subject_artifact_version_id`);
    return entry as ImpactGap;
  });
  const gapKeys = gaps.map((entry) => `${entry.code}\u0000${entry.subject_artifact_version_id ?? ""}`);
  if (gapKeys.some((entry, index) => index > 0 && gapKeys[index - 1]! >= entry)) fail(`${path}.completeness.gaps`, "expected sorted unique gaps");
  if (gaps.some((entry) => entry.subject_artifact_version_id !== null && !subjectIds.has(entry.subject_artifact_version_id))) {
    fail(`${path}.completeness.gaps`, "subject-scoped gaps must identify an emitted visible subject");
  }
  let redacted = false;
  if (completenessHeader.redaction_terminal !== null) {
    const redaction = exactRecord(completenessHeader.redaction_terminal, `${path}.completeness.redaction_terminal`, ["terminal_kind", "reason"]);
    literal(redaction.terminal_kind, `${path}.completeness.redaction_terminal.terminal_kind`, ["redacted"]);
    literal(redaction.reason, `${path}.completeness.redaction_terminal.reason`, ["restricted_evidence_omitted"]);
    redacted = true;
  }
  const allGapCodes = new Set(gaps.map((entry) => entry.code));
  const traversalGapCodes = new Set<ImpactGapCode>([
    "max_depth_reached", "max_records_reached", "max_paths_reached", "max_path_steps_reached",
    "cycle_detected", "unknown_record_type", "unknown_relationship_type", "unsupported_semantics",
  ]);
  const propagationGapCodes = new Set<ImpactGapCode>([
    ...traversalGapCodes, "state_history_incomplete", "effective_time_unavailable",
  ]);
  const lifecycleGapCodes = new Set<ImpactGapCode>([
    "state_history_incomplete", "effective_time_unavailable", "invalidation_receipt_unavailable",
    "unknown_record_type", "unknown_relationship_type", "unsupported_semantics",
  ]);
  const intersects = (left: ReadonlySet<ImpactGapCode>, right: ReadonlySet<ImpactGapCode>) => [...left].some((code) => right.has(code));
  const expectedCompleteness = [
    redacted || intersects(allGapCodes, traversalGapCodes) ? "incomplete" : "complete",
    redacted || intersects(allGapCodes, propagationGapCodes) ? "incomplete" : "complete",
    redacted || intersects(allGapCodes, lifecycleGapCodes) ? "incomplete" : "complete",
  ];
  if (traversal !== expectedCompleteness[0] || propagationCompleteness !== expectedCompleteness[1] || lifecycleCompleteness !== expectedCompleteness[2]) {
    fail(`${path}.completeness`, "axes must be derived from exact gaps and redaction");
  }
  for (const [index, entry] of subjects.entries()) {
    const subjectId = entry.subject_ref.artifact_version_id;
    const relevantGapCodes = new Set(gaps
      .filter((gap) => gap.subject_artifact_version_id === null || gap.subject_artifact_version_id === subjectId)
      .map((gap) => gap.code));
    if (entry.propagation_status === "stopped" && (redacted || intersects(relevantGapCodes, propagationGapCodes))) {
      fail(`${path}.subjects[${index}].propagation_status`, "stopped requires a complete relevant propagation probe");
    }
    if (entry.explicit_invalidation_status === "not_recorded" && (redacted || intersects(relevantGapCodes, lifecycleGapCodes))) {
      fail(`${path}.subjects[${index}].explicit_invalidation_status`, "not_recorded requires complete relevant lifecycle evidence");
    }
    if (entry.explicit_invalidation_status === "indeterminate" && !redacted && !intersects(relevantGapCodes, lifecycleGapCodes)) {
      fail(`${path}.subjects[${index}].explicit_invalidation_status`, "indeterminate requires a relevant lifecycle gap or redaction");
    }
    const expectedPathIds = paths.filter((candidate) => candidate.subject_artifact_version_id === subjectId).map((candidate) => candidate.path_id).sort();
    const expectedExclusionIds = exclusions.filter((candidate) => candidate.subject_artifact_version_id === subjectId).map((candidate) => candidate.exclusion_id).sort();
    if (JSON.stringify(entry.path_ids) !== JSON.stringify(expectedPathIds)) fail(`${path}.subjects[${index}].path_ids`, "must equal all emitted paths for the subject");
    if (JSON.stringify(entry.exclusion_ids) !== JSON.stringify(expectedExclusionIds)) fail(`${path}.subjects[${index}].exclusion_ids`, "must equal all emitted exclusions for the subject");
    const expectedMinimumDepth = Math.min(...entry.path_ids.map((pathId) => pathsById.get(pathId)!.hops.length));
    if (entry.minimum_depth !== expectedMinimumDepth) fail(`${path}.subjects[${index}].minimum_depth`, "must equal the shortest emitted path depth");
    const statuses = new Set(entry.path_ids.map((pathId) => pathsById.get(pathId)!.propagation_status));
    const expectedStatus = statuses.has("enabled") ? "enabled" : statuses.size === 1 && statuses.has("stopped") ? "stopped" : "indeterminate";
    const conservativeIndeterminate = expectedStatus === "stopped"
      && entry.propagation_status === "indeterminate"
      && (redacted || intersects(relevantGapCodes, propagationGapCodes));
    if (entry.propagation_status !== expectedStatus && !conservativeIndeterminate) fail(`${path}.subjects[${index}].propagation_status`, "must be derived from the emitted paths");
    if (entry.invalidation_evidence?.cause_path_ids.some((pathId) => (
      !entry.path_ids.includes(pathId) || pathsById.get(pathId)?.propagation_status !== "enabled"
    ))) fail(`${path}.subjects[${index}].invalidation_evidence.cause_path_ids`, "must identify enabled paths belonging to this subject");
    if (entry.invalidation_evidence && entry.invalidation_evidence.caused_by_artifact_version_id !== root.id) {
      fail(`${path}.subjects[${index}].invalidation_evidence.caused_by_artifact_version_id`, "must identify the selected root cause");
    }
    if (entry.invalidation_evidence) {
      const evidence = entry.invalidation_evidence;
      if (evidence.effective_at > effectiveAsOf) {
        fail(`${path}.subjects[${index}].invalidation_evidence.effective_at`, "must not exceed snapshot.effective_as_of");
      }
      if ([evidence.recorded_at, evidence.receipt.recorded_at].some((value) => value > recordedAsOf)) {
        fail(`${path}.subjects[${index}].invalidation_evidence.recorded_at`, "system times must not exceed snapshot.recorded_as_of");
      }
    }
  }
  for (const [index, entry] of exclusions.entries()) {
    const targetPath = pathsById.get(entry.path_id);
    if (!targetPath || targetPath.subject_artifact_version_id !== entry.subject_artifact_version_id || targetPath.propagation_status !== "stopped") {
      fail(`${path}.exclusions[${index}]`, "must reference a stopped path for the same subject");
    }
    const stoppedHop = targetPath.hops[entry.step_ordinal];
    const exactStop = entry.reason === "non_propagating_binding"
      ? stoppedHop?.propagates_invalidity === false
      : entry.reason === "binding_pinned"
        ? stoppedHop?.binding_state.state === "pinned"
        : stoppedHop?.operation_state.state === "pinned";
    if (entry.step_ordinal !== targetPath.hops.length - 1 || entry.reason !== targetPath.terminal.reason || !exactStop) {
      fail(`${path}.exclusions[${index}]`, "must identify the final decisive stopped hop and reason");
    }
  }
  for (const [index, entry] of paths.entries()) {
    const matchingExclusions = exclusionsByPathId.get(entry.path_id) ?? [];
    const expectedCount = entry.propagation_status === "stopped" ? 1 : 0;
    if (matchingExclusions.length !== expectedCount) {
      fail(`${path}.paths[${index}]`, "stopped paths require exactly one exclusion and non-stopped paths require none");
    }
  }

  const summary = exactRecord(item.summary, `${path}.summary`, [
    "root_excluded", "reachable_count", "propagation_closure_count", "propagation_enabled_count",
    "explicitly_invalidated_count", "potential_only_count", "stopped_count", "indeterminate_count", "exclusion_count",
  ]);
  if (summary.root_excluded !== true) fail(`${path}.summary.root_excluded`, "expected true");
  const expectedSummary: Omit<ImpactSummary, "root_excluded"> = {
    reachable_count: subjects.length,
    propagation_closure_count: subjects.filter((entry) => entry.propagation_status === "enabled").length,
    propagation_enabled_count: subjects.filter((entry) => entry.propagation_status === "enabled").length,
    explicitly_invalidated_count: subjects.filter((entry) => entry.explicit_invalidation_status === "recorded").length,
    potential_only_count: subjects.filter((entry) => entry.propagation_status === "enabled" && entry.explicit_invalidation_status === "not_recorded").length,
    stopped_count: subjects.filter((entry) => entry.propagation_status === "stopped").length,
    indeterminate_count: subjects.filter((entry) => entry.propagation_status === "indeterminate" || entry.explicit_invalidation_status === "indeterminate").length,
    exclusion_count: exclusions.length,
  };
  for (const [key, expected] of Object.entries(expectedSummary)) {
    if (integer(summary[key], `${path}.summary.${key}`, 0, key === "exclusion_count" ? 5000 : 500) !== expected) {
      fail(`${path}.summary.${key}`, `expected derived count ${expected}`);
    }
  }

  return value as ImpactAnalysisDocument;
}

function parseResult(value: unknown, path: string): ImpactAnalysisResult {
  const item = exactRecord(value, path, ["document", "semantic_identity"]);
  parseDocument(item.document, `${path}.document`);
  const semanticIdentity = exactRecord(item.semantic_identity, `${path}.semantic_identity`, [
    "digest", "canonicalizer", "canonicalizer_version", "byte_length", "media_type",
  ]);
  digest(semanticIdentity.digest, `${path}.semantic_identity.digest`);
  literal(semanticIdentity.canonicalizer, `${path}.semantic_identity.canonicalizer`, ["rfc8785"]);
  literal(semanticIdentity.canonicalizer_version, `${path}.semantic_identity.canonicalizer_version`, ["1"]);
  integer(semanticIdentity.byte_length, `${path}.semantic_identity.byte_length`, 1, Number.MAX_SAFE_INTEGER);
  literal(semanticIdentity.media_type, `${path}.semantic_identity.media_type`, ["application/vnd.thread.impact.v2+json"]);

  return value as ImpactAnalysisResult;
}

export function parseImpactAnalysisEnvelope(value: unknown): ImpactAnalysisEnvelope {
  const item = exactRecord(value, "$", ["schema_version", "request_id", "data", "page", "provenance", "warnings"]);
  literal(item.schema_version, "$.schema_version", ["thread.api.v1"]);
  uuid(item.request_id, "$.request_id");
  if (item.page !== null) fail("$.page", "expected null for a non-paginated analysis");
  parseResult(item.data, "$.data");
  const provenance = exactRecord(item.provenance, "$.provenance", ["source", "projection_version", "schema_migration"]);
  literal(provenance.source, "$.provenance.source", ["thread_core"]);
  literal(provenance.projection_version, "$.provenance.projection_version", [IMPACT_ANALYSIS_CONTRACT_VERSION]);
  const schemaMigration = text(provenance.schema_migration, "$.provenance.schema_migration", 14);
  if (!/^\d{14}$/.test(schemaMigration) || schemaMigration < IMPACT_ANALYSIS_MIN_SCHEMA_MIGRATION) {
    fail("$.provenance.schema_migration", `expected a migration marker at or after ${IMPACT_ANALYSIS_MIN_SCHEMA_MIGRATION}`);
  }
  if (array(item.warnings, "$.warnings", 0).length) fail("$.warnings", "expected no transport warnings for typed impact analysis");
  return value as ImpactAnalysisEnvelope;
}

function validUnicodeScalarString(value: string, path: string): void {
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code >= 0xd800 && code <= 0xdbff) {
      const next = value.charCodeAt(index + 1);
      if (!(next >= 0xdc00 && next <= 0xdfff)) fail(path, "JCS cannot encode an unpaired Unicode surrogate");
      index += 1;
    } else if (code >= 0xdc00 && code <= 0xdfff) {
      fail(path, "JCS cannot encode an unpaired Unicode surrogate");
    }
  }
}

function canonicalJson(value: unknown, path = "$canonical"): string {
  if (value === null || typeof value === "boolean") return String(value);
  if (typeof value === "number") {
    if (!Number.isFinite(value) || !Number.isSafeInteger(value)) fail(path, "JCS identity requires a finite safe integer");
    return JSON.stringify(value);
  }
  if (typeof value === "string") {
    validUnicodeScalarString(value, path);
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) return `[${value.map((entry, index) => canonicalJson(entry, `${path}[${index}]`)).join(",")}]`;
  if (typeof value !== "object") fail(path, "expected a JSON value");
  const item = value as UnknownRecord;
  return `{${Object.keys(item).sort().map((key) => {
    validUnicodeScalarString(key, `${path}.<key>`);
    return `${JSON.stringify(key)}:${canonicalJson(item[key], `${path}.${key}`)}`;
  }).join(",")}}`;
}

function throwIfAborted(signal?: AbortSignal): void {
  if (!signal?.aborted) return;
  if (typeof signal.throwIfAborted === "function") signal.throwIfAborted();
  throw new DOMException("Impact analysis verification was aborted.", "AbortError");
}

async function sha256Identity(value: unknown): Promise<string> {
  if (!globalThis.crypto?.subtle) fail("$crypto", "WebCrypto SHA-256 is required to verify impact identities");
  const bytes = new TextEncoder().encode(canonicalJson(value));
  const result = await globalThis.crypto.subtle.digest("SHA-256", bytes);
  return `sha256:${[...new Uint8Array(result)].map((byte) => byte.toString(16).padStart(2, "0")).join("")}`;
}

/** Verify every content-derived path and exclusion identity before UI state. */
export async function verifyImpactAnalysisIdentities(
  envelope: ImpactAnalysisEnvelope,
  options: { signal?: AbortSignal } = {},
): Promise<void> {
  const checks: Array<{ path: string; expected: string; payload: unknown }> = [
    ...envelope.data.document.paths.map((entry, index) => {
      const { ordinal: _ordinal, path_id: expected, ...payload } = entry;
      return { path: `$.data.document.paths[${index}].path_id`, expected, payload };
    }),
    ...envelope.data.document.exclusions.map((entry, index) => {
      const { exclusion_id: expected, ...payload } = entry;
      return { path: `$.data.document.exclusions[${index}].exclusion_id`, expected, payload };
    }),
  ];
  let next = 0;
  const worker = async () => {
    while (next < checks.length) {
      throwIfAborted(options.signal);
      const check = checks[next++]!;
      if (await sha256Identity(check.payload) !== check.expected) {
        fail(check.path, "does not identify its exact RFC 8785 semantic payload");
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(8, checks.length) }, () => worker()));
  throwIfAborted(options.signal);
  const identity = envelope.data.semantic_identity;
  const semanticBytes = new TextEncoder().encode(canonicalJson(envelope.data.document));
  if (semanticBytes.byteLength !== identity.byte_length) {
    fail("$.data.semantic_identity.byte_length", "must equal the RFC 8785 impact payload byte length");
  }
  if (await sha256Identity(envelope.data.document) !== identity.digest) {
    fail("$.data.semantic_identity.digest", "must equal SHA-256 of the RFC 8785 impact payload");
  }
  throwIfAborted(options.signal);
}

function normalizedInstantMicros(value: string, path: string): bigint {
  if (!validAwareTimestampComponents(value)) fail(path, "expected a valid aware instant");
  const match = AWARE_TIMESTAMP_PARTS.exec(value)!;
  const wholeSecond = `${match[1]}-${match[2]}-${match[3]}T${match[4]}:${match[5]}:${match[6]}${match[8]}`;
  const wholeSecondMillis = Date.parse(wholeSecond);
  if (!Number.isFinite(wholeSecondMillis)) fail(path, "expected a valid aware instant");
  const micros = BigInt(`${match[7] ?? ""}000000`.slice(0, 6));
  return BigInt(wholeSecondMillis) * 1000n + micros;
}

function validateResponseBinding(envelope: ImpactAnalysisEnvelope, request: ImpactAnalysisRequest): void {
  const snapshot = envelope.data.document.snapshot;
  if (snapshot.project_id !== request.project_id || snapshot.root_ref.id !== request.root_ref.id) {
    fail("$.data.document.snapshot", "must identify the exact requested project and ArtifactVersion");
  }
  const expectedLimits: ImpactAnalysisLimits = {
    max_depth: request.max_depth ?? IMPACT_ANALYSIS_DEFAULT_LIMITS.max_depth,
    max_records: request.max_records ?? IMPACT_ANALYSIS_DEFAULT_LIMITS.max_records,
    max_paths: request.max_paths ?? IMPACT_ANALYSIS_DEFAULT_LIMITS.max_paths,
    max_path_steps: request.max_path_steps ?? IMPACT_ANALYSIS_DEFAULT_LIMITS.max_path_steps,
  };
  for (const key of Object.keys(expectedLimits) as Array<keyof ImpactAnalysisLimits>) {
    if (snapshot.limits[key] !== expectedLimits[key]) fail(`$.data.document.snapshot.limits.${key}`, "must equal the requested/defaulted limit");
  }
  if (request.as_of && normalizedInstantMicros(snapshot.effective_as_of, "$.data.document.snapshot.effective_as_of") !== normalizedInstantMicros(request.as_of, "request.as_of")) {
    fail("$.data.document.snapshot.effective_as_of", "must equal the requested as_of instant");
  }
  if (request.recorded_as_of !== undefined && snapshot.recorded_as_of !== request.recorded_as_of) {
    fail("$.data.document.snapshot.recorded_as_of", "must equal request.recorded_as_of");
  }
  if (request.snapshot_token !== undefined && snapshot.snapshot_token !== request.snapshot_token) {
    fail("$.data.document.snapshot.snapshot_token", "must equal request.snapshot_token");
  }
}

export async function fetchImpactAnalysis(
  request: ImpactAnalysisRequest,
  options: { signal?: AbortSignal } = {},
): Promise<ImpactAnalysisEnvelope> {
  const normalized = normalizeImpactAnalysisRequest(request);
  const value = await apiClient<unknown>(IMPACT_ANALYSIS_PATH, {
    method: "POST",
    body: normalized,
    signal: options.signal,
  });
  const envelope = parseImpactAnalysisEnvelope(value);
  validateResponseBinding(envelope, normalized);
  await verifyImpactAnalysisIdentities(envelope, options);
  return envelope;
}

function exportFilename(
  response: Response,
  request: ImpactAnalysisExportRequest,
  format: ImpactAnalysisExportFormat,
): string {
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const match = /filename="([^"\\/]+)"/i.exec(disposition);
  return match?.[1] ?? `impact-analysis-${request.root_ref.id}.${format}`;
}

async function exportErrorDetail(response: Response): Promise<string> {
  try {
    const value = await response.clone().json() as { error?: { message?: unknown }; detail?: unknown };
    if (typeof value.error?.message === "string") return value.error.message;
    if (typeof value.detail === "string") return value.detail;
  } catch {
    // Export proxies are allowed to return a non-JSON error body.
  }
  return response.text().catch(() => "Unknown error");
}

/** Export the exact displayed impact document, guarded by its semantic digest. */
export async function exportImpactAnalysis(
  request: ImpactAnalysisExportRequest,
  format: ImpactAnalysisExportFormat,
  options: { signal?: AbortSignal } = {},
): Promise<ImpactAnalysisExport> {
  validateImpactAnalysisExportRequest(request);
  const normalizedRequest: ImpactAnalysisExportRequest = {
    ...request,
    as_of: normalizeInstantToUtcMicroseconds(request.as_of),
    project_id: request.project_id.toLowerCase(),
    root_ref: { entity_kind: "artifact_version", id: request.root_ref.id.toLowerCase() },
  };
  literal(format, "format", ["json", "html"]);
  const token = await getAuthToken();
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  const apiBase = import.meta.env.VITE_API_URL ?? "/api";
  const response = await fetch(`${apiBase}${IMPACT_ANALYSIS_EXPORT_PATH}?format=${format}`, {
    method: "POST",
    credentials: "include",
    headers,
    body: JSON.stringify(normalizedRequest),
    signal: options.signal,
  });
  if (response.status === 409) throw new ImpactAnalysisChangedError();
  if (!response.ok) throw new Error(`API ${response.status}: ${await exportErrorDetail(response)}`);
  const expectedMediaType = format === "json" ? "application/vnd.thread.impact.v2+json" : "text/html";
  const mediaType = response.headers.get("Content-Type") ?? "";
  if (!hasExactMediaType(mediaType, expectedMediaType)) {
    throw new ImpactAnalysisValidationError("response.Content-Type", `expected ${expectedMediaType}`);
  }
  const responseDigest = response.headers.get("X-Thread-Impact-Digest");
  if (responseDigest !== normalizedRequest.expected_semantic_digest) {
    throw new ImpactAnalysisValidationError("response.X-Thread-Impact-Digest", "must match the displayed semantic digest");
  }
  let bytes: Uint8Array<ArrayBuffer>;
  try {
    bytes = await readBoundedResponseBytes(
      response,
      format === "json" ? IMPACT_ANALYSIS_MAX_DOCUMENT_CANONICAL_BYTES : IMPACT_ANALYSIS_MAX_HTML_BYTES,
    );
    if (format === "json") {
      await verifyJsonExportBytes(bytes, normalizedRequest.expected_semantic_digest);
    } else {
      verifyOfflineHtmlBytes(bytes, [
        "<h1>Thread impact analysis</h1>",
        normalizedRequest.expected_semantic_digest,
        normalizedRequest.project_id,
        normalizedRequest.root_ref.id,
        normalizedRequest.as_of,
        normalizedRequest.recorded_as_of,
        normalizedRequest.snapshot_token,
      ]);
    }
  } catch (error) {
    if (error instanceof ExportIntegrityError) {
      throw new ImpactAnalysisValidationError("response.body", error.message);
    }
    throw error;
  }
  return {
    blob: exportBlob(bytes, mediaType),
    filename: exportFilename(response, normalizedRequest, format),
    mediaType,
    semanticDigest: responseDigest,
  };
}
